import React, { useRef, useState } from "react";
import clsx from "clsx";
import { App, Button, Segmented, Space, theme } from "antd";
import Tooltip from "../../../Common/Tooltip";
import { ProCard } from "@ant-design/pro-components";
import {
  AppstoreOutlined,
  EditOutlined,
  HolderOutlined,
  RedoOutlined,
  SettingOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDndContext,
  useDroppable,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import LetterBlock from "./LetterBlock";
import BlocksPanel from "./BlocksPanel";
import BlockEditor from "./BlockEditor";
import LetterSheet, { Device } from "./LetterSheet";
import SaveDraftDialog from "../SaveDraftDialog";
import ViewSwitches from "../ViewSwitches";
import PageHead from "../../../Common/PageHead";
import DeleteButton from "../../../Common/DeleteButton";
import Send from "../Send";
import StylePanel from "./StylePanel";
import {
  Block,
  BlockType,
  createBlock,
  defaultEmailStyles,
  defaultLetter,
  sectionTypeKey,
  EmailStyles,
  LETTER_LANGUAGES,
  LetterLanguage,
  templateBlocks,
  templateStyles,
} from "../blocks";
import { EmailTemplate } from "../data";
import { CampaignAction } from "..";

import styles from "./index.module.scss";
import { useScrolledUnder } from "../../../Common/StickyBar";

type BuilderTab = "blocks" | "edit" | "settings";

/** What one step of the letter's history holds: all of the letter. */
interface LetterState {
  blocks: Block[];
  styles: EmailStyles;
}

/**
 * The end of the letter, as somewhere to drop into.
 *
 * A section dropped on another goes in front of it, which leaves no way to say
 * "after the last one" — this strip is that way. It is only a target: it draws
 * a line while something is over it and nothing otherwise.
 */
function LetterEnd() {
  const { setNodeRef, isOver } = useDroppable({ id: "letter-end" });
  // Only while something is being dragged: a target that is always there is
  // 48px of empty sheet under the last section for the rest of the time.
  const { active } = useDndContext();

  return (
    <div
      ref={setNodeRef}
      className={styles.letterEnd}
      style={{ height: active ? 48 : 0 }}
    >
      {isOver ? <span aria-hidden className={styles.letterEndLine} /> : null}
    </div>
  );
}

/** The two screens the flow moves through, both sharing one letter. */
export type BuilderStep = "build" | "send";

interface BuilderProps {
  step: BuilderStep;
  /**
   * Which action opened the builder. The screen is the same one either way —
   * only what it is called changes, and it has to agree with the breadcrumb
   * that got the user here.
   */
  mode: CampaignAction;
  onStep: (step: BuilderStep) => void;
  /** Leaves the flow entirely, back to the list of campaigns. */
  onExit: () => void;
  /** What the letter starts as; null is the builder's own default letter. */
  template: EmailTemplate | null;
  /**
   * What the campaign is already called. An existing draft brings its own
   * subject, and saving it again does not ask for one; a new campaign has none
   * until it is saved, and is asked then.
   */
  subject: string;
  /** Who it goes to, where the campaign it was opened from named someone. */
  audience?: string;
  /**
   * Deletes the campaign being edited. Undefined where there is nothing to
   * delete — a new campaign, or a copy, which is a new one.
   */
  onDelete?: () => void;
  /**
   * Whether the letter holds work that is not saved as a template. The section
   * asks before leaving when it does — the builder only reports it, since it is
   * the section that owns navigation.
   */
  onDirty: (dirty: boolean) => void;
}

export default function Builder({
  step,
  mode,
  onStep,
  onExit,
  template,
  subject,
  audience,
  onDelete,
  onDirty,
}: BuilderProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  // The initialiser runs once per visit to the flow: the builder is mounted
  // when the section leaves its list and unmounted when it returns, so a letter
  // picked from a template is laid out exactly once and nothing later resets it.
  const [blocks, setBlocks] = useState<Block[]>(() =>
    template
      ? (templateBlocks[template.id]?.() ?? defaultLetter())
      : defaultLetter(),
  );
  const [past, setPast] = useState<LetterState[]>([]);
  const [future, setFuture] = useState<LetterState[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<BuilderTab>("blocks");
  // The subject the draft is being saved under, and the dialog that asks for
  // it.
  const [saving, setSaving] = useState(false);
  const [draftSubject, setDraftSubject] = useState(subject);
  const [device, setDevice] = useState<Device>("desktop");
  const [language, setLanguage] = useState<LetterLanguage>(LETTER_LANGUAGES[0]);
  const panelScroll = useScrolledUnder();
  const canvasScroll = useScrolledUnder();
  // What is being dragged, so the pointer can carry a label for it instead of
  // the section itself.
  const [dragging, setDragging] = useState<BlockType | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  // A few pixels of travel before a drag starts, so the same button can still
  // be clicked to append a section.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  const { modal, message } = App.useApp();
  // Seeded from the theme, then owned by the user: the initialiser runs once,
  // so nothing the style panel sets is overwritten later.
  /**
   * Whether anything in this letter has been touched since it was opened.
   *
   * The same answer the section keeps for its own guard, kept here as well
   * because the builder needs it for a question of its own — whether replacing
   * the letter is worth asking about. Set through `markDirty` so the two can
   * never say different things.
   */
  const [touched, setTouched] = useState(false);
  const markDirty = (value: boolean) => {
    setTouched(value);
    onDirty(value);
  };

  const [emailStyles, setEmailStyles] = useState<EmailStyles>(() => {
    const defaults = defaultEmailStyles(token);

    // A template may answer for the letter as well as for its sections — see
    // templateStyles. Everything it does not name stays the letter's own.
    return {
      ...defaults,
      ...(template ? (templateStyles[template.id]?.(defaults) ?? {}) : {}),
    };
  });

  /**
   * One step of the letter's history: its sections and the styles they are
   * drawn in.
   *
   * Both, because both are the letter. History held only the sections, so a
   * palette worked out over several minutes could be lost with nothing to take
   * it back — which is why Reset to defaults used to have to ask, or offer an
   * undo of its own. Undo is that undo.
   */
  const snapshot = () => ({ blocks, styles: emailStyles });

  const restore = ({
    blocks: theirBlocks,
    styles,
  }: ReturnType<typeof snapshot>) => {
    setBlocks(theirBlocks);
    setEmailStyles(styles);
  };

  /** Every mutation goes through here, so undo never misses one. */
  const commit = (next: Block[]) => {
    setPast((stack) => [...stack, snapshot()]);
    setFuture([]);
    setBlocks(next);
    markDirty(true);
  };

  /** The same, for a change to the letter itself rather than to a section. */
  const commitStyles = (patch: Partial<EmailStyles>) => {
    setPast((stack) => [...stack, snapshot()]);
    setFuture([]);
    setEmailStyles((current) => ({ ...current, ...patch }));
    markDirty(true);
  };

  // A step back or forward can move, add or remove any section, so the
  // selection cannot survive it — and the editor with nothing selected says
  // only that nothing is selected. The panel goes back to Blocks, the same way
  // it does when a section is removed or the canvas is clicked.
  const clearSelection = () => {
    setSelected(null);
    setTab("blocks");
  };

  const undo = () => {
    const previous = past[past.length - 1];
    if (!previous) return;
    setPast((stack) => stack.slice(0, -1));
    setFuture((stack) => [snapshot(), ...stack]);
    restore(previous);
    clearSelection();
  };

  const redo = () => {
    const [next, ...rest] = future;
    if (!next) return;
    setPast((stack) => [...stack, snapshot()]);
    setFuture(rest);
    restore(next);
    clearSelection();
  };

  const patchSelected = (patch: Partial<Block>) =>
    commit(blocks.map((b) => (b.id === selected ? { ...b, ...patch } : b)));

  // A letter is never empty: when one section is left, it cannot be removed.
  // The controls that would remove it are disabled rather than silently doing
  // nothing, and the guard is repeated in the handlers because both the panel
  // and the block's own toolbar lead here.
  const canRemove = blocks.length > 1;

  // Whichever control removed it — the panel's button or the section's own
  // toolbar — the editor is left describing a section that is gone, so the
  // panel goes back to Blocks. Both paths lead here so they cannot drift.
  const removeBlock = (id: string | null) => {
    if (!canRemove) return;
    commit(blocks.filter((b) => b.id !== id));
    setSelected(null);
    setTab("blocks");
  };

  const removeSelected = () => removeBlock(selected);

  const moveBlock = (id: string, direction: -1 | 1) => {
    const from = blocks.findIndex((b) => b.id === id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    commit(next);
  };

  const duplicateBlock = (id: string) => {
    const index = blocks.findIndex((b) => b.id === id);
    const source = blocks[index];
    if (!source) return;
    const copy = { ...source, ...createBlock(source.type), text: source.text };
    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    commit(next);
    setSelected(copy.id);
  };

  // A drag that starts in the Blocks panel carries `new:<type>`; one that
  // starts in the letter carries the block's own id.
  const newType = (id: string) =>
    id.startsWith("new:") ? (id.slice(4) as BlockType) : null;

  const onDragStart = ({ active }: DragStartEvent) => {
    const id = String(active.id);
    setDragging(newType(id) ?? blocks.find((b) => b.id === id)?.type ?? null);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragging(null);
    if (!over || active.id === over.id) return;

    const created = newType(String(active.id));

    // Dropped from the panel: the section is made where it landed — before the
    // block under the pointer, or at the end of the letter when that is the
    // strip below the last one.
    if (created) {
      const block = createBlock(created);
      const at = blocks.findIndex((b) => b.id === over.id);
      const next = [...blocks];

      next.splice(at === -1 ? next.length : at, 0, block);
      commit(next);
      setSelected(block.id);
      setTab("edit");

      // A section dropped near the edge of the canvas can land half out of
      // sight; the canvas is brought to it after paint, since the element does
      // not exist until React has rendered the new state.
      requestAnimationFrame(() => {
        canvasRef.current
          ?.querySelector(`[data-block-id="${block.id}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });

      return;
    }

    const from = blocks.findIndex((b) => b.id === active.id);
    const to = blocks.findIndex((b) => b.id === over.id);
    if (from === -1 || to === -1) return;

    const next = [...blocks];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    commit(next);
    setSelected(String(active.id));
  };

  const applyTemplate = (template: EmailTemplate) => {
    const replace = () => {
      setBlocks(templateBlocks[template.id]?.() ?? defaultLetter());
      // A template replacing the letter brings its own answer for the letter
      // too, the same as one opening it.
      setEmailStyles((current) => ({
        ...current,
        ...(templateStyles[template.id]?.(current) ?? {}),
      }));
      setPast([]);
      setFuture([]);
      setSelected(null);
      setTab("blocks");
      markDirty(false);
    };

    // Nothing written yet, nothing to lose: a letter still exactly as its
    // template left it is not work, and asking to throw it away makes trying
    // the templates on feel like a decision each time.
    if (!touched) {
      replace();

      return;
    }

    // Past that, a template replaces the letter and takes its history with it,
    // so there is nothing to undo afterwards — that is worth a question first.
    modal.confirm({
      title: t("pages.campaigns.builder.replaceTitle"),
      content: t("pages.campaigns.builder.replaceText"),
      okText: t("pages.campaigns.builder.replaceOk"),
      okButtonProps: { danger: true },
      onOk: replace,
    });
  };

  const selectTab = (next: BuilderTab) => {
    setTab(next);
    // Leaving the editor drops the selection, which hides the block toolbar.
    if (next !== "edit") setSelected(null);
  };

  // The send step is rendered here rather than beside the builder so the letter
  // survives the trip: the blocks, their history and the letter's styles all
  // live in this component, and unmounting it to show another screen would lose
  // them. Sending ends the flow — the message says so and the list is where the
  // user lands.
  if (step === "send") {
    return (
      <Send
        blocks={blocks}
        emailStyles={emailStyles}
        onBack={() => onStep("build")}
        subject={draftSubject}
        audience={audience}
        // Through markDirty, not straight to onDirty. The send step's fields
        // are part of the same unsaved campaign as the letter, and reporting
        // them only upwards left the builder's own `touched` false: coming
        // back from a half-filled send step and picking another template
        // replaced the letter without asking, because this half of the flow
        // believed nothing had been done.
        onDirty={markDirty}
        // A sent campaign has nothing left to lose, so the flow is left clean
        // — the same way saving a draft leaves it. This was `onExit` alone,
        // which returned to the list with the letter still marked unsaved: the
        // campaign had gone out and the next click on any menu item still
        // asked whether to discard it.
        onSent={() => {
          markDirty(false);
          onExit();
        }}
        onSaveDraft={() => {
          markDirty(false);
          onExit();
        }}
      />
    );
  }

  // Heading and body are siblings, not nested: Layout treats each direct child
  // of the content area as its own card with 16px below it. `transparent` keeps
  // these two out of that treatment, since the cards are inside them.
  return (
    <>
      {/* Named for the action that opened it, not always "Create": the same
          screen edits and copies, and the breadcrumb above already says
          which. */}
      <PageHead title={t(`pages.campaigns.heading.${mode}`)}>
        <Space>
          {/* Only on a campaign that exists — a new one and a copy have
              nothing behind them yet. */}
          {onDelete ? <DeleteButton onClick={onDelete} /> : null}

          <Button
            // A campaign that already has a subject is saved under it. The
            // dialog exists to give an unnamed draft a name, since the list
            // shows drafts by subject — asking again for one the user chose
            // earlier is a question with a known answer.
            onClick={() => {
              const named = draftSubject.trim();

              if (!named) {
                setSaving(true);

                return;
              }

              message.success(
                t("pages.campaigns.builder.draftSaved", { name: named }),
              );
              markDirty(false);
            }}
          >
            {t("common.saveAsDraft")}
          </Button>
          <Button type="primary" onClick={() => onStep("send")}>
            {t("pages.campaigns.builder.previewAndSend")}
          </Button>
        </Space>
      </PageHead>

      <SaveDraftDialog
        open={saving}
        subject={draftSubject}
        onCancel={() => setSaving(false)}
        onSaved={(subject) => {
          setSaving(false);
          setDraftSubject(subject);
          markDirty(false);
        }}
      />

      {/* One drag context for the whole builder, not just the letter: a section
          is dragged out of the Blocks panel and dropped into the letter, so
          both have to be inside it. */}
      <DndContext
        // closestCorners, not closestCenter. A letter's sections are nothing
        // like the same height — a paragraph is 60px, an Image + text block is
        // 300 — and centre-to-centre measuring meant that while a tall section
        // was being dragged, its centre stayed nearer the centres of the other
        // tall ones than of the short section actually under the pointer. Short
        // sections between tall ones could not be landed on at all.
        //
        // Corner-to-corner sums the distance between matching corners instead,
        // so a section wins when the dragged one overlaps it, whatever either
        // is worth in height. dnd-kit's own documentation names this one as the
        // algorithm for sortable lists, for exactly this reason.
        collisionDetection={closestCorners}
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setDragging(null)}
      >
        <div
          className={clsx("transparent", "flush-bottom", styles.layout)}
          style={
            {
              "--builder-border": token.colorBorderSecondary,
              // The builder's own highlight — the hover edge on a block button.
              // It is the admin panel's primary, and deliberately not the
              // letter's own accent, which the user edits.
              "--builder-accent": token.colorPrimary,
              // What the builder draws *on* the letter: the ring around a
              // selected section and the line showing where a dragged one would
              // land. Both say the same thing, so both are the same colour —
              // one step darker than the primary, since on a sheet where the
              // letter's own links and brand-filled buttons are already blue,
              // the primary itself did not read as the builder speaking.
              //
              // That step is antd's own token: colorPrimaryActive is primary-7,
              // the colour it darkens a primary button to while it is pressed.
              "--builder-selection": token.colorPrimaryActive,
              "--builder-icon": token.colorIcon,
            } as React.CSSProperties
          }
        >
          <ProCard className={styles.panel}>
            {/* The tab bar is outside the scrolling pane, so it stays in view
                while the blocks below it scroll — and takes the shadow every
                bar in the app takes once something is hidden under it. */}
            <div className={styles.tabsBar} style={panelScroll.shadow}>
              <Segmented<BuilderTab>
                block
                value={tab}
                onChange={selectTab}
                // Icons alone say too little for a control this important, and
                // a label beside each one would not fit the panel. The tip is
                // the label, on the same delay the block toolbar uses.
                options={[
                  {
                    value: "blocks",
                    label: (
                      <Tooltip title={t("pages.campaigns.builder.blocks")}>
                        {/* The tip belongs to the whole tab, so its trigger
                            fills it rather than wrapping the glyph alone. */}
                        <span className={styles.tabLabel}>
                          <AppstoreOutlined />
                        </span>
                      </Tooltip>
                    ),
                  },
                  {
                    value: "edit",
                    label: (
                      <Tooltip title={t("pages.campaigns.builder.edit")}>
                        {/* The tip belongs to the whole tab, so its trigger
                            fills it rather than wrapping the glyph alone. */}
                        <span className={styles.tabLabel}>
                          <EditOutlined />
                        </span>
                      </Tooltip>
                    ),
                  },
                  {
                    value: "settings",
                    label: (
                      <Tooltip title={t("pages.campaigns.builder.settings")}>
                        {/* The tip belongs to the whole tab, so its trigger
                            fills it rather than wrapping the glyph alone. */}
                        <span className={styles.tabLabel}>
                          <SettingOutlined />
                        </span>
                      </Tooltip>
                    ),
                  },
                ]}
              />
            </div>

            <div className={styles.pane} onScroll={panelScroll.onScroll}>
              {tab === "blocks" ? (
                <BlocksPanel onApplyTemplate={applyTemplate} />
              ) : null}

              {tab === "edit" ? (
                <BlockEditor
                  block={blocks.find((b) => b.id === selected)}
                  onChange={patchSelected}
                  canRemove={canRemove}
                  letterStyles={emailStyles}
                  onRemove={removeSelected}
                />
              ) : null}

              {tab === "settings" ? (
                <StylePanel value={emailStyles} onChange={commitStyles} />
              ) : null}
            </div>
          </ProCard>

          <ProCard className={styles.canvasCard}>
            {/* The letter scrolls under this row, so it takes the same shadow the
              panel's tab bar takes — shown only once something is under it. */}
            <div className={styles.toolbar} style={canvasScroll.shadow}>
              <div className={styles.toolbarCenter}>
                <Space.Compact>
                  <Button
                    icon={<UndoOutlined />}
                    disabled={past.length === 0}
                    onClick={undo}
                    aria-label={t("pages.campaigns.builder.undo")}
                  />
                  <Button
                    icon={<RedoOutlined />}
                    disabled={future.length === 0}
                    onClick={redo}
                    aria-label={t("pages.campaigns.builder.redo")}
                  />
                </Space.Compact>

                <ViewSwitches
                  device={device}
                  onDevice={setDevice}
                  language={language}
                  onLanguage={setLanguage}
                />
              </div>
            </div>

            <div
              ref={canvasRef}
              className={styles.canvas}
              onScroll={canvasScroll.onScroll}
              onClick={() => {
                setSelected(null);
                setTab("blocks");
              }}
            >
              {/* A white band the height of the letter, with the letter centred
                in it. Below it the canvas shows through, which is where the
                letter ends. The sheet is the letter's base ground — the same
                one a section painted "Base" sits on, so a letter with no
                section covering it reads as one surface. */}
              <LetterSheet
                device={device}
                background={emailStyles.background}
                pageBackground={emailStyles.emailBackground}
                fontFamily={emailStyles.fontFamily}
              >
                <SortableContext
                  items={blocks.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {blocks.map((block, position) => (
                    <LetterBlock
                      key={block.id}
                      block={block}
                      emailStyles={emailStyles}
                      preview={false}
                      selected={block.id === selected}
                      onSelect={() => {
                        setSelected(block.id);
                        setTab("edit");
                      }}
                      onMove={(direction) => moveBlock(block.id, direction)}
                      onDuplicate={() => duplicateBlock(block.id)}
                      canRemove={canRemove}
                      canMoveUp={position > 0}
                      canMoveDown={position < blocks.length - 1}
                      onRemove={() => removeBlock(block.id)}
                    />
                  ))}
                </SortableContext>

                {/* The strip below the last section: dropping a new one here
                    puts it at the end of the letter, which nothing else can
                    say — every other target is a section to land in front of. */}
                <LetterEnd />
              </LetterSheet>
            </div>
          </ProCard>
        </div>

        {/* Under the pointer: a label saying what is being moved, rather than a
            copy of the section. snapCenterToCursor puts it under the pointer —
            without it the overlay keeps the offset from wherever the section
            was grabbed, which for a full-width block is far off to the left. */}
        {/* `transparent` because this wrapper is now a direct child of the page
            content, which Layout paints as a card — a white band the width of
            the page appeared under the pointer while dragging. */}
        <DragOverlay
          className="transparent"
          dropAnimation={null}
          modifiers={[snapCenterToCursor]}
        >
          {dragging ? (
            <span className={styles.dragChip}>
              <HolderOutlined />
              {t(`pages.campaigns.blockTypes.${sectionTypeKey(dragging)}`)}
            </span>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
}
